/**
 * Resize an image file before upload to reduce storage and bandwidth
 */

import { MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT, IMAGE_QUALITY } from "@tabipla/shared";

export interface ResizeOptions {
  /** Maximum width in pixels (default: 1920) */
  maxWidth?: number;
  /** Maximum height in pixels (default: 1920) */
  maxHeight?: number;
  /** JPEG quality 0-1 (default: 0.85) */
  quality?: number;
  /** Output format (default: "image/jpeg") */
  format?: "image/jpeg" | "image/png" | "image/webp";
}

const DEFAULT_OPTIONS: Required<ResizeOptions> = {
  maxWidth: MAX_IMAGE_WIDTH,
  maxHeight: MAX_IMAGE_HEIGHT,
  quality: IMAGE_QUALITY,
  format: "image/jpeg",
};

/**
 * Resize an image file if it exceeds the maximum dimensions
 * @param file - The original image file
 * @param options - Resize options
 * @returns A resized File, or the original if no resize needed
 */
export async function resizeImage(file: File, options: ResizeOptions = {}): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Only process images
  if (!file.type.startsWith("image/")) {
    return file;
  }

  // Skip GIFs (preserve animation)
  if (file.type === "image/gif") {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const { width, height } = img;

      // Check if resize is needed
      if (width <= opts.maxWidth && height <= opts.maxHeight) {
        // No resize needed, but still convert to target format for consistency
        if (file.type === opts.format) {
          resolve(file);
          return;
        }
      }

      // Calculate new dimensions maintaining aspect ratio
      let newWidth = width;
      let newHeight = height;

      if (width > opts.maxWidth) {
        newWidth = opts.maxWidth;
        newHeight = (height * opts.maxWidth) / width;
      }

      if (newHeight > opts.maxHeight) {
        newHeight = opts.maxHeight;
        newWidth = (width * opts.maxHeight) / height;
      }

      // Create canvas and draw resized image
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(newWidth);
      canvas.height = Math.round(newHeight);

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }

          // Create new file with original name but new extension
          const ext = opts.format.split("/")[1];
          const baseName = file.name.replace(/\.[^.]+$/, "");
          const newFile = new File([blob], `${baseName}.${ext}`, {
            type: opts.format,
            lastModified: Date.now(),
          });

          resolve(newFile);
        },
        opts.format,
        opts.quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}
